import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchUserLicenses } from '../../store/slices/licenseSlice';
import warningIcon from '../../assets/icons/warning.svg';

const REFETCH_INTERVAL_MS = 30000;

const UnpaidInvoiceBanner: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const subscriptions = useAppSelector((state) => state.licenses.items);

  const unpaidSubscriptions = subscriptions.filter((s) => s.hasUnpaidInvoice);
  const hasUnpaid = unpaidSubscriptions.length > 0;

  // Unblocking after payment is async (Stripe `invoice.paid` webhook), so while
  // the banner is visible we refetch subscriptions on tab focus and every 30s
  // until the flag clears.
  useEffect(() => {
    if (!hasUnpaid) {
      return;
    }
    const refetch = () => {
      dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const intervalId = window.setInterval(refetch, REFETCH_INTERVAL_MS);
    return () => {
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [dispatch, hasUnpaid]);

  if (!hasUnpaid) {
    return null;
  }

  return (
    <div className="unpaid-invoice-banner">
      {unpaidSubscriptions.map((subscription) => (
        <div key={subscription.id} className="unpaid-invoice-banner__row">
          <div className="unpaid-invoice-banner__icon">
            <img src={warningIcon} alt="attention" />
          </div>
          <div className="unpaid-invoice-banner__text">
            {t('unpaid_invoice.description', { productName: subscription.productName })}
            {!subscription.unpaidInvoiceUrl && (
              <> {t('unpaid_invoice.contact_support')}</>
            )}
          </div>
          {subscription.unpaidInvoiceUrl && (
            <button
              className="unpaid-invoice-banner__pay-btn"
              onClick={() => window.open(subscription.unpaidInvoiceUrl as string, '_blank')}
            >
              {t('unpaid_invoice.pay_button')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default UnpaidInvoiceBanner;
